// frontend/src/components/production-sequence/OrderComponentsDrawer.tsx
// Drawer lateral derecho: detalle por componente de la OT seleccionada. Cabecera
// "hero" con KPIs, tabla con disponible antes/después y próxima entrada, resaltado
// en rojo de los componentes en falta, y footer con la fecha propuesta de
// reprogramación (cuándo se cubren TODOS los faltantes con las entradas previstas).
import { useEffect, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useFmtArticulo } from '@/hooks/useFmtArticulo';
import { consumosApi } from '@/services/api';
import { summarizeComponentesRecalc } from '@/utils/sequenceCalculations';
import { coberturaEntradas, type CoberturaEntradas } from '@/utils/entradasCobertura';
import { fmtNum, fmtDate, COMP_BADGE, STATUS_BADGE_BASE } from './sequenceFormat';
import { BarraMarcas, CabeceraMarca, MarcaCelda, useMarcada, useMarcasFilas } from '@/components/common/MarcasFilas';
import { claveFila } from '@/utils/marcasFilas';
import type { ProductionOrder, OrderComponentRecalc } from '@/types/productionSequence';
import type { EntradaCompraRow, MarcasFilas } from '@/types';

// 10 columnas de datos + el canalón de marcado.
const COL_COUNT = 11;

interface EntradaState { isLoading: boolean; isError: boolean; cob: CoberturaEntradas }

function compKey(c: OrderComponentRecalc): string {
  return [c.itemId, c.inventcolorid ?? '', c.inventstyleid ?? '', c.inventlocationid ?? ''].join('|');
}

function maxIso(dates: (string | null)[]): string | null {
  return dates.reduce<string | null>((max, d) => (d && (!max || d > max) ? d : max), null);
}

interface Props {
  open: boolean;
  order: ProductionOrder | null;   // OT en vivo (derivada por id desde el hook)
  dark: boolean;
  onClose: () => void;
}

export function OrderComponentsDrawer({ open, order, dark, onClose }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Los componentes son SUBLÍNEAS de la OT, así que comparten el ámbito de la
  // Secuencia y su clave cuelga de la clave de la OT.
  const marcas = useMarcasFilas('secuencia');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => { setExpanded(new Set()); }, [order?.id]);

  // ── Hooks SIEMPRE en el tope (antes del early return) ────────────────────────
  const comps = order?.componentesRecalc ?? [];
  const empresa = order?.empresa ?? '';
  const faltaComps = comps.filter(c => c.falta > 0);

  const results = useQueries({
    queries: faltaComps.map(c => ({
      queryKey: ['entradas-compra', c.itemId, c.inventcolorid ?? null, c.inventstyleid ?? null, empresa],
      queryFn: () => consumosApi.getEntradasCompra({
        itemid: c.itemId,
        inventcolorid: c.inventcolorid ?? undefined,
        inventstyleid: c.inventstyleid ?? undefined,
        dataareaid: empresa,
      }),
      enabled: open,
      staleTime: 10 * 60 * 1000,
    })),
  });

  const entradaByKey = new Map<string, EntradaState>();
  faltaComps.forEach((c, i) => {
    const r = results[i];
    const rows = (r?.data?.data ?? []) as EntradaCompraRow[];
    entradaByKey.set(compKey(c), { isLoading: !!r?.isLoading, isError: !!r?.isError, cob: coberturaEntradas(rows, c.falta) });
  });

  if (!open || !order) return null;

  const sum = summarizeComponentesRecalc(comps);
  const enFaltaCount = faltaComps.length;
  const coberturaPct = comps.length ? Math.round(((comps.length - enFaltaCount) / comps.length) * 100) : 100;

  // ── Fecha propuesta de reprogramación (a nivel OT) ───────────────────────────
  const faltaStates = faltaComps.map(c => entradaByKey.get(compKey(c))!);
  const anyLoading = faltaStates.some(s => s.isLoading);
  const coveredDates = faltaStates.map(s => s.cob.coveredDate);
  const allCovered = coveredDates.length > 0 && coveredDates.every(d => d != null);
  const propuesta = allCovered ? maxIso(coveredDates) : null;

  const panel = dark ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-700';

  return (
    <>
      <div className="plan-overlay-enter fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <aside
        className={`plan-drawer-enter fixed inset-y-0 right-0 z-50 w-[96vw] max-w-[1280px] border-l shadow-2xl flex flex-col ${panel} ${dark ? 'border-white/10' : 'border-slate-200'}`}
      >
        <Hero order={order} sum={sum} enFaltaCount={enFaltaCount} coberturaPct={coberturaPct} dark={dark} onClose={onClose} />

        <BarraMarcas dark={dark} marcas={marcas} className="shrink-0" />

        {/* Tabla */}
        <div className="flex-1 overflow-auto">
          {comps.length === 0 ? (
            <p className={`p-6 text-[13px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{t('sequence.components.empty')}</p>
          ) : (
            <table className="w-full border-collapse text-left">
              <TableHead dark={dark} />
              <tbody>
                {comps.map((c) => {
                  const key = compKey(c);
                  return (
                    <ComponentRow
                      key={key}
                      comp={c}
                      dark={dark}
                      marcas={marcas}
                      // `compKey` ya identifica al componente por artículo,
                      // color, estilo y almacén; el prefijo de la OT impide que
                      // el mismo material salga marcado en otra orden.
                      clave={claveFila(order.id, key)}
                      isOpen={expanded.has(key)}
                      onToggle={() => setExpanded(prev => {
                        const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s;
                      })}
                      entrada={c.falta > 0 ? entradaByKey.get(key) : undefined}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer: reprogramación */}
        <RescheduleFooter
          dark={dark}
          hasFalta={enFaltaCount > 0}
          loading={anyLoading}
          propuesta={propuesta}
          fechaActual={order.fechaPlanificadaFin}
          bloqueantes={coveredDates.filter(d => d == null).length}
        />
      </aside>
    </>
  );
}

// ── Cabecera hero con KPIs ─────────────────────────────────────────────────────
function Hero({ order, sum, enFaltaCount, coberturaPct, dark, onClose }: {
  order: ProductionOrder;
  sum: { count: number; rotura: number; faltaTotal: number };
  enFaltaCount: number;
  coberturaPct: number;
  dark: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fmtArt = useFmtArticulo();
  const heroBg = dark
    ? 'bg-gradient-to-br from-slate-800/80 via-slate-900 to-slate-900'
    : 'bg-gradient-to-br from-sky-50 via-white to-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const iconBtn = `p-1.5 rounded-lg cursor-pointer ${dark ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`;

  return (
    <div className={`relative border-b ${dark ? 'border-white/10' : 'border-slate-200'} ${heroBg}`}>
      {/* Acento lateral */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${enFaltaCount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} />
      <div className="px-6 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xl leading-none">🧩</span>
              <h2 className={`text-[18px] font-black tracking-tight ${dark ? 'text-slate-50' : 'text-slate-900'}`}>{order.id}</h2>
              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>{order.lineId}</span>
              {order.empresa && <span className={`text-[11px] ${muted}`}>· {order.empresa}</span>}
            </div>
            <p className={`mt-1 text-[12px] truncate ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
              <span className="font-semibold">{fmtArt(order.referencia)}</span>{order.descripcion ? ` · ${order.descripcion}` : ''}
            </p>
          </div>
          <button onClick={onClose} className={iconBtn} aria-label={t('sequence.components.entradas.collapse')}>
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l10 10M13 3L3 13" /></svg>
          </button>
        </div>

        {/* KPIs */}
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Kpi dark={dark} label={t('sequence.components.kpi.pending')} value={`${fmtNum(order.udPendientes)}`} tone="neutral" />
          <Kpi dark={dark} label={t('sequence.components.kpi.total')} value={`${sum.count}`} tone="neutral" />
          <Kpi dark={dark} label={t('sequence.components.kpi.short')} value={`${enFaltaCount}`} tone={enFaltaCount > 0 ? 'red' : 'green'} />
          <Kpi dark={dark} label={t('sequence.components.kpi.coverage')} value={`${coberturaPct}%`} tone={coberturaPct >= 100 ? 'green' : coberturaPct >= 60 ? 'amber' : 'red'} />
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, dark }: {
  label: string; value: string; tone: 'neutral' | 'green' | 'amber' | 'red'; dark: boolean;
}) {
  const toneCls: Record<string, string> = {
    neutral: dark ? 'text-slate-100' : 'text-slate-800',
    green: dark ? 'text-emerald-400' : 'text-emerald-600',
    amber: dark ? 'text-amber-400' : 'text-amber-600',
    red: dark ? 'text-red-400' : 'text-red-500',
  };
  const card = dark ? 'bg-white/5 border-white/10' : 'bg-white/70 border-slate-200';
  return (
    <div className={`rounded-xl border px-3 py-2 backdrop-blur-sm ${card}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{label}</div>
      <div className={`text-[18px] font-black tabular-nums leading-tight ${toneCls[tone]}`}>{value}</div>
    </div>
  );
}

function TableHead({ dark }: { dark: boolean }) {
  const { t } = useTranslation();
  const thCls = `px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`;
  return (
    <thead className={`sticky top-0 z-10 ${dark ? 'bg-slate-800' : 'bg-slate-50'} shadow-sm`}>
      <tr>
        <CabeceraMarca />
        <th className={thCls}>{t('sequence.components.col.component')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.bom')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.reserved')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.pending')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.availableBefore')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.availableAfter')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.assigned')}</th>
        <th className={`${thCls} text-right`}>{t('sequence.components.col.short')}</th>
        <th className={thCls}>{t('sequence.components.col.nextEntry')}</th>
        <th className={`${thCls} text-center`}>{t('sequence.components.col.status')}</th>
      </tr>
    </thead>
  );
}

// ── Fila de componente ─────────────────────────────────────────────────────────
function ComponentRow({ comp: c, dark, marcas, clave, isOpen, onToggle, entrada }: {
  comp: OrderComponentRecalc;
  dark: boolean;
  marcas: MarcasFilas;
  clave: string;
  isOpen: boolean;
  onToggle: () => void;
  entrada?: EntradaState;
}) {
  const { t } = useTranslation();
  const fmtArt = useFmtArticulo();
  // La fila lleva `onClick` propio (despliega las entradas de compra); el botón
  // de marcar ya corta la propagación, así que no se disparan los dos.
  const marcada = useMarcada(marcas, clave);

  const enFalta = c.falta > 0;
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const tdCls = `px-3 py-2 text-[12px] ${dark ? 'text-slate-300' : 'text-slate-700'}`;
  const rowBorder = dark ? 'border-white/5' : 'border-slate-100';
  const negCls = dark ? 'text-red-400' : 'text-red-500';
  const rowBg = enFalta
    ? (dark ? 'bg-red-950/25 hover:bg-red-950/40' : 'bg-red-50/60 hover:bg-red-50')
    : (dark ? 'hover:bg-white/3' : 'hover:bg-slate-50/70');

  const dims = [c.inventcolorid, c.inventstyleid].filter(Boolean) as string[];
  const proxima = entrada?.cob.futuras[0];

  return (
    <Fragment>
      <tr className={`border-t ${rowBorder} ${rowBg} ${marcada ? (dark ? 'fila-marcada-dark' : 'fila-marcada') : ''} ${enFalta ? 'cursor-pointer' : ''} transition-colors`} onClick={enFalta ? onToggle : undefined}>
        <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
        <td className={tdCls}>
          <div className="flex items-center gap-1 flex-wrap">
            {enFalta && <span className={`text-[9px] w-3 shrink-0 text-center ${negCls}`}>{isOpen ? '▾' : '▸'}</span>}
            <span className="font-semibold">{fmtArt(c.itemId)}</span>
            {dims.map((d, di) => (
              <span key={di} className={`text-[10px] px-1 py-px rounded ${dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>{d}</span>
            ))}
            {c.esGranel && (
              <span className={`text-[10px] px-1 py-px rounded ${dark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'}`}>
                {t('sequence.components.granel')}
              </span>
            )}
          </div>
        </td>
        <td className={`${tdCls} text-right tabular-nums`}>{fmtNum(c.consumoEstimado)}</td>
        <td className={`${tdCls} text-right tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmtNum(c.reserva)}</td>
        <td className={`${tdCls} text-right tabular-nums`}>{fmtNum(c.pendiente)}</td>
        <td className={`${tdCls} text-right tabular-nums ${c.disponibleAntes < 0 ? negCls : ''}`}>{fmtNum(c.disponibleAntes)}</td>
        <td className={`${tdCls} text-right tabular-nums font-semibold ${c.disponibleDespues < 0 ? negCls : (dark ? 'text-emerald-400' : 'text-emerald-600')}`}>{fmtNum(c.disponibleDespues)}</td>
        <td className={`${tdCls} text-right tabular-nums`}>{fmtNum(c.asignado)}</td>
        <td className={`${tdCls} text-right tabular-nums font-semibold ${enFalta ? negCls : muted}`}>{enFalta ? fmtNum(c.falta) : '—'}</td>
        <td className={`${tdCls} whitespace-nowrap`}>
          {!enFalta ? <span className={muted}>—</span>
            : entrada?.isLoading ? <span className={muted}>…</span>
            : entrada?.isError ? <span className={negCls}>!</span>
            : proxima ? (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <span aria-hidden>📦</span>
                <span className={dark ? 'text-emerald-400' : 'text-emerald-600'}>+{fmtNum(proxima.qtyEntrada)}</span>
                <span className={muted}>· {fmtDate(proxima.fechaEntrada ?? null)}</span>
              </span>
            ) : <span className={muted}>—</span>}
        </td>
        <td className={`${tdCls} text-center`}>
          <span className={`${STATUS_BADGE_BASE} ${COMP_BADGE[c.estado]}`}>{t(`sequence.componentStatus.${c.estado}`)}</span>
        </td>
      </tr>
      {enFalta && isOpen && entrada && (
        <tr className={dark ? 'bg-slate-950/40' : 'bg-slate-50/80'}>
          <td colSpan={COL_COUNT} className="p-0">
            <EntradasDetalle cob={entrada.cob} falta={c.falta} isLoading={entrada.isLoading} isError={entrada.isError} dark={dark} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ── Desglose de entradas de un componente ──────────────────────────────────────
function EntradasDetalle({ cob, falta, isLoading, isError, dark }: {
  cob: CoberturaEntradas; falta: number; isLoading: boolean; isError: boolean; dark: boolean;
}) {
  const { t } = useTranslation();
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const box = 'pl-9 pr-4 py-3';

  if (isLoading) return <div className={`${box} text-[11px] ${muted}`}>{t('sequence.components.entradas.loading')}</div>;
  if (isError) return <div className={`${box} text-[11px] text-red-500`}>{t('sequence.components.entradas.error')}</div>;

  return (
    <div className={box}>
      <div className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${muted}`}>{t('sequence.components.entradas.title')}</div>
      {cob.coveredDate ? (
        <div className={`text-[12px] font-semibold mb-2 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          {t('sequence.components.entradas.covered', { date: fmtDate(cob.coveredDate) })}
        </div>
      ) : (
        <div className={`text-[12px] font-semibold mb-2 ${dark ? 'text-amber-400' : 'text-amber-600'}`}>
          {t('sequence.components.entradas.notCovered', { have: fmtNum(cob.totalEntrante), need: fmtNum(falta) })}
        </div>
      )}
      {cob.futuras.length === 0 ? (
        <div className={`text-[11px] ${muted}`}>{t('sequence.components.entradas.none')}</div>
      ) : (
        <ul className="flex flex-col gap-1">
          {cob.futuras.map((e, i) => {
            const isCover = cob.coveredIndex === i;
            const coverCls = isCover
              ? (dark ? 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200')
              : (dark ? 'text-slate-300' : 'text-slate-700');
            return (
              <li key={`${e.purchid}-${i}`} className={`flex items-center gap-2 text-[11px] tabular-nums rounded px-1.5 py-0.5 ${coverCls}`}>
                <span aria-hidden>📦</span>
                <span className={`px-1 py-px rounded text-[10px] ${dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>{e.tipo}</span>
                <span className="font-mono">{e.purchid}</span>
                <span className={dark ? 'text-emerald-400' : 'text-emerald-600'}>+{fmtNum(e.qtyEntrada)}</span>
                <span className={muted}>· {fmtDate(e.fechaEntrada ?? null)}</span>
                {e.inventlocationid && <span className={muted}>· {e.inventlocationid}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Footer: propuesta de reprogramación ────────────────────────────────────────
function RescheduleFooter({ dark, hasFalta, loading, propuesta, fechaActual, bloqueantes }: {
  dark: boolean; hasFalta: boolean; loading: boolean; propuesta: string | null; fechaActual: string | null; bloqueantes: number;
}) {
  const { t } = useTranslation();
  const wrap = `px-6 py-3.5 border-t ${dark ? 'border-white/10' : 'border-slate-200'}`;

  // Sin faltas → producible con stock actual.
  if (!hasFalta) {
    return (
      <div className={wrap}>
        <Callout dark={dark} tone="green" icon="✅" title={t('sequence.components.reschedule.canProduce')} />
      </div>
    );
  }
  if (loading) {
    return <div className={wrap}><Callout dark={dark} tone="neutral" icon="⏳" title={t('sequence.components.reschedule.calculating')} /></div>;
  }
  if (propuesta) {
    return (
      <div className={wrap}>
        <Callout
          dark={dark} tone="amber" icon="📅"
          title={t('sequence.components.reschedule.proposed')}
          value={fmtDate(propuesta)}
          hint={fechaActual ? t('sequence.components.reschedule.current', { date: fmtDate(fechaActual) }) : undefined}
        />
      </div>
    );
  }
  return (
    <div className={wrap}>
      <Callout dark={dark} tone="red" icon="⛔" title={t('sequence.components.reschedule.notPossible')} hint={t('sequence.components.reschedule.blocking', { count: bloqueantes })} />
    </div>
  );
}

function Callout({ dark, tone, icon, title, value, hint }: {
  dark: boolean; tone: 'green' | 'amber' | 'red' | 'neutral'; icon: string; title: string; value?: string; hint?: string;
}) {
  const toneBox: Record<string, string> = {
    green: dark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200',
    amber: dark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200',
    red: dark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200',
    neutral: dark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200',
  };
  const toneTxt: Record<string, string> = {
    green: dark ? 'text-emerald-400' : 'text-emerald-700',
    amber: dark ? 'text-amber-400' : 'text-amber-700',
    red: dark ? 'text-red-400' : 'text-red-600',
    neutral: dark ? 'text-slate-300' : 'text-slate-600',
  };
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${toneBox[tone]}`}>
      <span className="text-lg leading-none">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className={`text-[12px] font-semibold ${toneTxt[tone]}`}>{title}</div>
        {hint && <div className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{hint}</div>}
      </div>
      {value && <div className={`text-[20px] font-black tabular-nums ${toneTxt[tone]}`}>{value}</div>}
    </div>
  );
}
